// noinspection ES6PreferShortImport

import getLogger from '@ignitesales/logger-es';
import ignite from '../../services';
import { triggerIgniteAction } from '../Analytics';
import { parsePhoneNumber } from '../../utils/helpers/formatHelper';
import cognitoAuth from '../../utils/cognitoAuth.mjs';
import GuideApiService from '../../services/GuideApiService.mjs';
import { makeRequestAxios } from '../_utils/requestAxios.mjs';

const logger = getLogger({ module: 'contact-action-creators' });

const ContactMeActions = {
  CONTACTME_LEAD_UPDATE_STARTED: 'CONTACTME_LEAD_UPDATE_STARTED',
  CONTACTME_LEAD_UPDATED: 'CONTACTME_LEAD_UPDATED',
  APPOINTMENT_TIMESLOT_SELECTED: 'APPOINTMENT_TIMESLOT_SELECTED',
  APPOINTMENT_TIMESLOTS_UPDATE_STARTED: 'APPOINTMENT_TIMESLOTS_UPDATE_STARTED',
  APPOINTMENT_TIMESLOTS_UPDATED: 'APPOINTMENT_TIMESLOTS_UPDATED',
  APPOINTMENT_LOCATION_SEARCH_UPDATED: 'APPOINTMENT_LOCATION_SEARCH_UPDATED',
  APPOINTMENT_LOCATION_SELECTED: 'APPOINTMENT_LOCATION_SELECTED',
  APPOINTMENT_LOCATIONS_UPDATE_STARTED: 'APPOINTMENT_LOCATIONS_UPDATE_STARTED',
  APPOINTMENT_LOCATIONS_UPDATED: 'APPOINTMENT_LOCATIONS_UPDATED',
  APPOINTMENT_BOOKING_STARTED: 'APPOINTMENT_BOOKING_STARTED',
  APPOINTMENT_BOOKING_COMPLETE: 'APPOINTMENT_BOOKING_COMPLETE',
  REMOTE_SESSION_CREATE_STARTED: 'REMOTE_SESSION_CREATE_STARTED',
  REMOTE_SESSION_CREATE_DONE: 'REMOTE_SESSION_CREATE_DONE',
  REMOTE_SESSION_COMPLETED_REQUEST_STARTED: 'REMOTE_SESSION_COMPLETED_REQUEST_STARTED',
  REMOTE_SESSION_COMPLETED_REQUEST_DONE: 'REMOTE_SESSION_COMPLETED_REQUEST_DONE',
  REMOTE_SESSION_REQUEST_FAILED: 'REMOTE_SESSION_REQUEST_FAILED',
  SEND_TO_LEADPROCESSOR_STARTED: 'SEND_TO_LEADPROCESSOR_STARTED',
  SEND_TO_LEADPROCESSOR_ERROR: 'SEND_TO_LEADPROCESSOR_ERROR',
  SEND_TO_LEADPROCESSOR_COMPLETED_COGNITO_AUTH: 'SEND_TO_LEADPROCESSOR_COMPLETED_COGNITO_AUTH',
  SEND_TO_LEADPROCESSOR_REQUEST_STARTED: 'SEND_TO_LEADPROCESSOR_REQUEST_STARTED',
  SEND_TO_LEADPROCESSOR_COMPLETED: 'SEND_TO_LEADPROCESSOR_COMPLETED',
  TRIGGER_ACTION_STARTED: 'TRIGGER_ACTIOPN_STARTED',
  TRIGGER_ACTION_ERROR: 'TRIGGER_ACTIOPN_ERROR',
  TRIGGER_ACTION_COMPLETED_COGNITO_AUTH: 'TRIGGER_ACTIOPN_COMPLETED_COGNITO_AUTH',
  TRIGGER_ACTION_REQUEST_STARTED: 'TRIGGER_ACTIOPN_REQUEST_STARTED',
  TRIGGER_ACTION_COMPLETED: 'TRIGGER_ACTIOPN_COMPLETED'
};

const appointmentBookingComplete = (appointment) => {
  return {
    type: ContactMeActions.APPOINTMENT_BOOKING_COMPLETE,
    appointment,
  };
};

const appointmentLocationSearchUpdated = (locationSearchForm) => {
  return {
    type: ContactMeActions.APPOINTMENT_LOCATION_SEARCH_UPDATED,
    locationSearchForm,
  };
};

const appointmentLocationSelected = (location) => {
  return {
    type: ContactMeActions.APPOINTMENT_LOCATION_SELECTED,
    location,
  };
};

const appointmentTimeSlotSelected = (time) => {
  return {
    type: ContactMeActions.APPOINTMENT_TIMESLOT_SELECTED,
    time,
  };
};

const bookAppointment = (appointment, cb) => {
  return async (dispatch) => {
    try {
      dispatch(processActionType(ContactMeActions.APPOINTMENT_BOOKING_STARTED));
      const result = await ignite.appointmentService.bookAppointment(
        appointment
      );
      cb(result.status);
      if (result.status) {
        await ignite.answerService.saveAnswer({
          appkey: 'appointmentId',
          value: result.id,
          timestamp: new Date(),
        });
        await ignite.answerService.saveAnswer({
          appkey: 'appointmentTime',
          value: appointment.time,
          timestamp: new Date(),
        });
        dispatch(appointmentBookingComplete(result));
        dispatch(triggerIgniteAction({ actionName: 'makeAppointmentClicked' }));
      }
    } catch (ex) {
      logger.error('An error occured while booking appointment', ex);
      cb(false);
    }
  };
};

const sendContactFieldsToAnswerService = (
  contactLead,
  fieldsForAnswerService = []
) => {
  if (fieldsForAnswerService.length > 0) {
    Object.keys(contactLead).forEach(async (field) => {
      if (fieldsForAnswerService.includes(field)) {
        await ignite.answerService.saveAnswer({
          appkey: field,
          value: contactLead[field],
          timestamp: new Date(),
        });
      }
    });
  }
};

const contactLeadUpdate = ({ contactLead, fieldsForAnswerService }) => {
  return async (dispatch) => {
    try {
      dispatch(processActionType(ContactMeActions.CONTACTME_LEAD_UPDATE_STARTED));
      sendContactFieldsToAnswerService(contactLead, fieldsForAnswerService);
      dispatch(triggerIgniteAction({ actionName: 'contactMeClicked' }));
      await ignite.contactService.submitContact(contactLead);
      const sendLeadResult = ignite.contactService.sendLead();
      if (sendLeadResult instanceof Promise) { // doing this for backwards compatibility
        await sendLeadResult;
      }
      dispatch(contactLeadUpdateComplete(contactLead));
    } catch (ex) {
      logger.error('Error while saving lead', ex);
    }
  };
};

const contactLeadUpdateComplete = (contactLead) => {
  return {
    type: ContactMeActions.CONTACTME_LEAD_UPDATED,
    contactLead,
  };
};

const getAvailableTimeSlots = (request) => {
  return async (dispatch) => {
    try {
      dispatch(processActionType(ContactMeActions.APPOINTMENT_TIMESLOTS_UPDATE_STARTED));
      const results = await ignite.appointmentService.getTimeSlots(request);
      const timeSlots = results.map((timeSlot) => {
        timeSlot.start = new Date(timeSlot.time);
        timeSlot.end = new Date(timeSlot.time);
        timeSlot.end.setHours(
          timeSlot.start.getHours(),
          timeSlot.start.getMinutes() + parseInt(timeSlot.length)
        );
        return timeSlot;
      });
      dispatch(setAvailableTimeSlots(timeSlots));
    } catch (ex) {
      logger.error('An error while loading time slots', ex);
    }
  };
};

const getLocations = ({ lat, lng }) => {
  return async (dispatch, getState) => {
    try {
      dispatch(
        processActionType(ContactMeActions.APPOINTMENT_LOCATIONS_UPDATE_STARTED)
      );
      const searchForLocationMatch = getState().contactMe
        .searchForLocationMatch;
      const locations = await ignite.appointmentService.getLocations({
        lat,
        lng,
        searchForLocationMatch,
      });
      dispatch(setLocations(locations || []));
    } catch (ex) {
      logger.error('An error while loading locations', ex);
    }
  };
};

const setAvailableTimeSlots = (availableTimeSlots) => {
  return {
    type: ContactMeActions.APPOINTMENT_TIMESLOTS_UPDATED,
    availableTimeSlots,
  };
};

const setLocations = (availableLocations) => {
  return {
    type: ContactMeActions.APPOINTMENT_LOCATIONS_UPDATED,
    availableLocations,
  };
};

const sendResultsViaEmail = ({ fromEmail, email, message, copyMe = false }) => {
  return async () => {
    try {
      await ignite.contactService.emailResults({
        from: {
          email: fromEmail || email,
        },
        to: {
          email,
        },
        message,
        copyMe,
      });
    } catch (ex) {
      logger.error('Error while emailing results', ex);
    }
  };
};

const sendResultsViaSms = (phoneNumber) => {
  return async () => {
    try {
      const parsedPhoneNumber = parsePhoneNumber(phoneNumber);
      await ignite.contactService.textResults(parsedPhoneNumber);
    } catch (ex) {
      logger.error('Error while sending results via text message', ex);
    }
  };
};

const createRemoteSession = ({ emailAddress = '', messagesObj, phoneNumber = '' }) => {
  return async (dispatch) => {
    try {
      dispatch(processActionType(ContactMeActions.REMOTE_SESSION_CREATE_STARTED));
      const defaultMsgObj = { bankerMessage: '', bankerSubject: '', customerMessage: '', customerSubject: '' };
      const messages = { ...defaultMsgObj, ...messagesObj };
      await ignite.userService.createRemoteSession({ ...messages, emailAddress, phoneNumber: phoneNumber ? parsePhoneNumber(phoneNumber) : '' });
      dispatch(processActionType(ContactMeActions.REMOTE_SESSION_CREATE_DONE));
    } catch (ex) {
      logger.error('Failed to create a remote session', ex);
      dispatch(
        processActionType(ContactMeActions.REMOTE_SESSION_REQUEST_FAILED)
      );
    }
  };
};

const completeRemoteSession = ({ messagesObj }) => {
  return async (dispatch, getState) => {
    const answers = getState().answers;
    if (answers['remoteSessionCompleted']) {
      logger.debug('Remote session already completed for this user');
    } else {
      try {
        dispatch( processActionType(ContactMeActions.REMOTE_SESSION_COMPLETED_REQUEST_STARTED) );
        const defaultMsgObj = { message: '', subject: ''};
        const messages = { ...defaultMsgObj, ...messagesObj };
        await ignite.userService.completeRemoteSession({ ...messages, url: '' });
        dispatch( processActionType(ContactMeActions.REMOTE_SESSION_COMPLETED_REQUEST_DONE) );
      } catch (ex) {
        logger.error('Failed to complete a remote session', ex);
        dispatch( processActionType(ContactMeActions.REMOTE_SESSION_REQUEST_FAILED) );
      }
    }
  };
};

const sendToLeadProcessor = async (cognitoConfig, command, lead = null) => {
  return async(dispatch) => {
    dispatch( processActionType(ContactMeActions.SEND_TO_LEADPROCESSOR_STARTED) );
    const token = await cognitoAuth(cognitoConfig);
    if(!token){
      dispatch( processActionType(ContactMeActions.SEND_TO_LEADPROCESSOR_ERROR) );
      throw new Error('Failed to get cognito response');
    }

    if(!command){
      dispatch( processActionType(ContactMeActions.SEND_TO_LEADPROCESSOR_ERROR) );
      throw new Error('No lead processor command to send');
    }
    dispatch( processActionType(ContactMeActions.SEND_TO_LEADPROCESSOR_COMPLETED_COGNITO_AUTH) );
    const headers = {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    const data = {...command};
    if(lead) data.lead = lead;
    const body = JSON.stringify(data);
    dispatch( processActionType(ContactMeActions.SEND_TO_LEADPROCESSOR_REQUEST_STARTED) );

    let axiosResponse;
    try {
      axiosResponse = await makeRequestAxios('POST', cognitoConfig.url, body, headers || []);
      dispatch( processActionType(ContactMeActions.SEND_TO_LEADPROCESSOR_COMPLETED) );
    } catch (error) {
      axiosResponse = error.message;
      dispatch( processActionType(ContactMeActions.SEND_TO_LEADPROCESSOR_ERROR) );
    }

    return axiosResponse;
  };
};

const extractIntegrationInputs = (integration, command, lead) => {
  const result = {};
  if(!integration.inputs || !integration.inputs[command])
    throw new Error(`Could not find integration inputs for command ${command}`);
  integration.inputs[command].forEach(e => {
    result[e.name] = processActionInputPlaceholders(e.value || e.defaultValue, lead);
  });
  return result;
};

const extractActionInputs = (action, lead) => {
  const result = {};
  if(!action.inputs)
    return result;
  action.inputs.forEach(e => {
    result[e.name] = processActionInputPlaceholders(e.value || e.defaultValue, lead);
  });
  return result;
};

//TODO: move this to a dedicated action service of some kind
//Need to decide what place the action system has in neuro
//for now this is the only file that uses it
const processActionInputPlaceholders = (value, lead) => {
  GuideApiService.useLead(lead);
  if(typeof value == 'string' && value == '${lead}') return lead;
  return GuideApiService.transformValueWithPlaceholders(value);
};

const saveActionMetadata = (key, data) => {
  if (typeof data == 'object')
    window.sessionStorage[`'ignite.actionsMetadata.${key}`] = JSON.stringify(data);
  else
    window.sessionStorage[`'ignite.actionsMetadata.${key}`] = data;
};

//field: 'inputs' || 'outputs'
const saveFields = (field, response) => {
  const savefield = `${field.replace(/s$/, '')}Saves`;
  for(let outputKey in response['$$ignite'][field]) {
    if(response['$$ignite'][savefield][outputKey] == 'answers') {
      window.ignite.answerService.saveAnswer({
        appkey: outputKey,
        value: response['$$ignite'][field][outputKey] || response[outputKey],
        timestamp: new Date()
      }).then();
    } else if (response['$$ignite'][savefield][outputKey] == 'session'){
      saveActionMetadata(outputKey, response['$$ignite'][field][outputKey]);
    }
  }
};

const triggerAction = async (dialogueApiConfig, action, lead) => {
  return async(dispatch) => {
    dispatch( processActionType(ContactMeActions.TRIGGER_ACTION_STARTED) );
    try{
      await GuideApiService.authenicate(dialogueApiConfig);
    }catch(e){
      dispatch( processActionType(ContactMeActions.TRIGGER_ACTION_ERROR) );
      throw e;
    }

    if(!action){
      dispatch( processActionType(ContactMeActions.TRIGGER_ACTION_ERROR) );
      throw new Error('No action to trigger');
    }
    dispatch( processActionType(ContactMeActions.TRIGGER_ACTION_COMPLETED_COGNITO_AUTH) );
    
    const integration = await GuideApiService.makeRequest('GET', `/integrations/name/${action.targetId}`);
    const data = {...extractIntegrationInputs(integration, action.command, lead), ...extractActionInputs(action || {inputs: []}, lead)};
 
    dispatch( processActionType(ContactMeActions.TRIGGER_ACTION_REQUEST_STARTED) );

    let axiosResponse;
    try {
      const trigger = action.triggers.find(e => e.type == 'contactSubmit');
      const url = `/actions/${action.name}/trigger/${trigger._id}`;

      axiosResponse = await GuideApiService.makeRequest('POST', url, data, GuideApiService.uuid);
      if (axiosResponse['$$ignite'] && axiosResponse['$$ignite'].outputs && axiosResponse['$$ignite'].outputSaves) {
        saveFields('outputs', axiosResponse);
      }
      if (axiosResponse['$$ignite'] && axiosResponse['$$ignite'].inputs && axiosResponse['$$ignite'].inputSaves) {
        saveFields('inputs', axiosResponse);
      }
      dispatch( processActionType(ContactMeActions.TRIGGER_ACTION_COMPLETED) );
    } catch (error) {
      axiosResponse = error.message;
      dispatch( processActionType(ContactMeActions.TRIGGER_ACTION_ERROR) );
    }

    return axiosResponse;
  };
};

const processActionType = (type) => {
  return { type };
};

export {
  appointmentBookingComplete,
  appointmentLocationSelected,
  appointmentLocationSearchUpdated,
  appointmentTimeSlotSelected,
  bookAppointment,
  completeRemoteSession,
  createRemoteSession,
  contactLeadUpdate,
  contactLeadUpdateComplete,
  ContactMeActions,
  getAvailableTimeSlots,
  getLocations,
  sendResultsViaEmail,
  sendResultsViaSms,
  setAvailableTimeSlots,
  setLocations,
  sendToLeadProcessor,
  triggerAction
};
